import Phaser from 'phaser';
import { COLORS, FONTS, GAME_WIDTH, TIER_COLORS, fontStr, microFontStr } from '@/config';
import { lagRundetRekt, type RundetRektProxy } from '@/ui/RoundedRect';
import { Kolonne } from '@/ui/layout';
import { gameState } from '@/state/store';
import type { GameStateData, Tier } from '@/state/GameState';
import { SESONG_NAVN, aaret, sesongForDag } from '@/systems/sesong';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { ariaMirror } from '@/ui/AriaMirror';
import { audioManager } from '@/systems/audioManager';
import { ambientAudio } from '@/systems/ambientAudio';
import { autoSpeed, type AutoSpeedNivaa } from '@/systems/autoSpeed';
import { sfx } from '@/systems/sfx';

// Runde 47: HUD rullet tilbake til ren Phaser-rendering. DOM-overlayet
// (HudDom) er fjernet siden DOM-koordinater og Phaser-koordinater (1080×1920
// FIT) ikke holdt seg synk på tvers av viewport-størrelser, noe som ga
// massive overlapp mellom DOM-HUD og Phaser-tekst.
//
// HUD-zonen er nå 96 px høy i Phaser-koordinater og inneholder alt:
// kroner, dag, sesong, tier-chip, omdømme-chip, mute-knapp og speed-knapp.
const HUD_HEIGHT = 96;

// Tier-chip-er bruker samme palett som elsewhere i UI-en. S og A får
// spesial-bakgrunn (gull / dyp-lilla) for ekstra glans på toppen.
const TIER_BG: Record<Tier, number> = {
  D: 0x6b6b6b,
  C: 0x8b5e3c,
  B: 0x862736,
  A: 0x4a2c4f,
  S: 0xb8860b,
};

const TIER_TEKST_FARGE: Record<Tier, string> = {
  D: '#ffffff',
  C: '#ffffff',
  B: '#ffffff',
  A: '#ffffff',
  S: '#3a2010',
};

function omdommeNivaaTekst(verdi: number): string {
  if (verdi >= 85) return 'Stjerne';
  if (verdi >= 60) return 'Anerkjent';
  if (verdi >= 30) return 'Etablert';
  return 'Ny';
}

function formaterKroner(n: number): string {
  return `${n.toLocaleString('nb-NO')} kr`;
}

function sesongTekst(dag: number): string {
  const s = sesongForDag(dag);
  return `${SESONG_NAVN[s]} år ${aaret(dag)}`;
}

/**
 * HUDScene — alle HUD-elementer rendres i Phaser slik at koordinatene
 * holder seg synk med scene-innholdet. Skjermlesere får fortsatt info
 * via `ariaMirror.varselHud(...)` (skjult ARIA-live-region i DOM) og
 * via HiddenActions-knappene som triggrer samme handler som mute-knappen.
 */
export class HUDScene extends Phaser.Scene {
  private kronerTekst?: Phaser.GameObjects.Text;
  private dagTekst?: Phaser.GameObjects.Text;
  private sesongTekst?: Phaser.GameObjects.Text;

  private tierChipBg?: RundetRektProxy;
  private tierChipTekst?: Phaser.GameObjects.Text;
  private tierGlow?: RundetRektProxy;
  private tierGlowTween?: Phaser.Tweens.Tween;

  private omdommeChipBg?: RundetRektProxy;
  private omdommeChipTekst?: Phaser.GameObjects.Text;

  private muteKnappBg?: RundetRektProxy;
  private muteKnappTekst?: Phaser.GameObjects.Text;
  private speedKnappBg?: RundetRektProxy;
  private speedKnappTekst?: Phaser.GameObjects.Text;
  private settingsKnappBg?: RundetRektProxy;

  private unsubscribe?: () => void;
  private unsubAutoSpeed?: () => void;

  constructor() {
    super('HUD');
  }

  create(): void {
    // Beige bakgrunns-stripe under HUD med skille-linje mot scene-innhold.
    this.add
      .rectangle(0, 0, GAME_WIDTH, HUD_HEIGHT, COLORS.beige)
      .setOrigin(0, 0)
      .setAlpha(0.96);
    this.add
      .rectangle(0, HUD_HEIGHT, GAME_WIDTH, 4, COLORS.wine)
      .setOrigin(0, 0)
      .setAlpha(0.9);

    this.byggStatRad();
    this.byggChipRad();
    this.byggKnapper();

    this.refresh(gameState.snapshot);
    this.unsubscribe = gameState.subscribe((snap) => this.refresh(snap));
    this.unsubAutoSpeed = autoSpeed.subscribe((n) => this.oppdaterSpeedSymbol(n));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.unsubAutoSpeed?.();
      this.unsubAutoSpeed = undefined;
      this.tierGlowTween?.stop();
      this.tierGlowTween = undefined;
    });
  }

  /**
   * Pulserer tier-chip-en med gull-glød. Brukes når tier oppgraderes.
   * Respekterer "redusert bevegelse"-innstillingen.
   */
  glodTierChip(varighetMs = 3000): void {
    if (!this.tierGlow) return;
    if (foretrekkerRedusertBevegelse()) return;
    this.tierGlowTween?.stop();
    this.tierGlow.setFillStyle(0xb8860b, 0);
    const proxy = { v: 0 };
    this.tierGlowTween = this.tweens.add({
      targets: proxy,
      v: { from: 0, to: 0.85 },
      duration: 500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: Math.max(0, Math.floor(varighetMs / 1000) - 1),
      onUpdate: () => {
        this.tierGlow?.setFillStyle(0xb8860b, proxy.v);
      },
      onComplete: () => {
        this.tierGlow?.setFillStyle(0xb8860b, 0);
      },
    });
  }

  // ---------- Bygg ----------

  /**
   * Øverste rad: tre stat-bokser (kroner, dag, sesong) jevnt fordelt
   * fra venstre. Hver boks har label + verdi.
   */
  private byggStatRad(): void {
    const padding = 22;
    const radY = 10;
    const boksHoyde = 48;
    // Reserverer plass til tre knapper (speed + mute + settings) helt til høyre.
    const knapperBredde = 3 * 56 + 2 * 12 + padding;
    const tilgjengelig = GAME_WIDTH - padding - knapperBredde;
    const boksBredde = Math.floor((tilgjengelig - 2 * 12) / 3);

    const lagStat = (i: number, label: string): Phaser.GameObjects.Text => {
      const x = padding + i * (boksBredde + 12);
      const bg = lagRundetRekt(this, x, radY, boksBredde, boksHoyde, COLORS.white, 0.94, 12);
      bg.setStrokeStyle(1, COLORS.oakDark, 0.25);

      // Mikro-label (2026-06-04): «KRONER»/«DAG»/«SESONG» bruker microFontStr
      // (~14px) i stedet for fontStr(11)=22px. Det doblet etiketten og presset
      // verdien ned i chip-raden. Label + verdi stables nå fra målt høyde, så
      // de aldri overlapper og verdien får klaring til chip-raden under.
      const labelTxt = this.add
        .text(x + 10, 0, label, {
          fontFamily: FONTS.hud,
          fontSize: microFontStr(11),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const verdi = this.add
        .text(x + 10, 0, '', {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const kol = new Kolonne(x + 10, radY + 2, 0);
      kol.legg(labelTxt);
      kol.legg(verdi);
      return verdi;
    };

    this.kronerTekst = lagStat(0, 'KRONER');
    this.dagTekst = lagStat(1, 'DAG');
    this.sesongTekst = lagStat(2, 'SESONG');
  }

  /**
   * Nederste rad i HUD-zonen: tier-chip + omdømme-chip på venstre side.
   * Tier-chip dispatcher 'hud:openTier' på document slik at
   * ForlagshusetScene kan åpne MinKarriereModal — samme mønster som
   * tidligere DOM-HUD brukte.
   */
  private byggChipRad(): void {
    const padding = 22;
    const radY = 62;
    const chipH = 28;

    // Skjult gull-glow bak tier-chip — brukes ved tier-oppgradering.
    const tierW = 110;
    const tierX = padding;
    this.tierGlow = lagRundetRekt(this, tierX - 4, radY - 4, tierW + 8, chipH + 8, 0xb8860b, 0, 14);

    this.tierChipBg = lagRundetRekt(this, tierX, radY, tierW, chipH, TIER_BG.D, 1, 14);
    this.tierChipBg.setStrokeStyle(2, COLORS.white, 0.9);
    this.tierChipTekst = this.add
      .text(tierX + tierW / 2, radY + chipH / 2, 'Tier D', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: TIER_TEKST_FARGE.D,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    this.tierChipBg.setInteractive({ useHandCursor: true });
    this.tierChipBg.on('pointerup', () => {
      sfx.play('click');
      document.dispatchEvent(new CustomEvent('hud:openTier'));
    });

    // Omdømme-chip — viser nivå-navn. Klikk er passivt (kun fokus),
    // ekte detaljvisning ligger i Min karriere-modalen.
    // Runde 48 (Sven): flyttet litt til høyre (gap 10→18) og bredere
    // (200→240) så «Omdømme: Etablert (50)» får plass uten å flyte utenfor.
    const omdW = 240;
    const omdX = tierX + tierW + 18;
    this.omdommeChipBg = lagRundetRekt(this, omdX, radY, omdW, chipH, COLORS.white, 0.94, 14);
    this.omdommeChipBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.omdommeChipTekst = this.add
      .text(omdX + omdW / 2, radY + chipH / 2, 'Omdømme: Ny', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(12),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0.5);
  }

  /**
   * Mute-knapp og speed-knapp helt til høyre, samme y-rad som chip-er.
   * Mute kjører toggleMute → ambient + voice-over følger med (logikken
   * ligger inni audioManager.toggleMute som varsler voiceOver via
   * settEksternBlokkering).
   */
  private byggKnapper(): void {
    const padding = 22;
    const knappH = 48;
    const knappW = 56;
    const knappY = 10;

    // Runde 48 (Sven): tannhjul-knapp ytterst til høyre som åpner Settings;
    // lyd- og speed-knappen flyttet ett hakk innover.
    const settingsX = GAME_WIDTH - padding - knappW;
    this.settingsKnappBg = lagRundetRekt(this, settingsX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.settingsKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.add
      .text(settingsX + knappW / 2, knappY + knappH / 2, '⚙', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.settingsKnappBg.setInteractive({ useHandCursor: true });
    this.settingsKnappBg.on('pointerup', () => {
      sfx.play('click');
      document.dispatchEvent(new CustomEvent('hud:openSettings'));
    });

    const muteX = settingsX - 12 - knappW;
    this.muteKnappBg = lagRundetRekt(this, muteX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.muteKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.muteKnappTekst = this.add
      .text(muteX + knappW / 2, knappY + knappH / 2, audioManager.erAv ? '🔇' : '🔊', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.muteKnappBg.setInteractive({ useHandCursor: true });
    this.muteKnappBg.on('pointerup', () => this.toggleMute());

    const speedX = muteX - 12 - knappW;
    this.speedKnappBg = lagRundetRekt(this, speedX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.speedKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.speedKnappTekst = this.add
      .text(speedX + knappW / 2, knappY + knappH / 2, autoSpeed.symbol(), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.speedKnappBg.setInteractive({ useHandCursor: true });
    this.speedKnappBg.on('pointerup', () => {
      autoSpeed.cycle();
      sfx.play('click');
      // Symbol oppdateres i autoSpeed.subscribe-listener.
    });
  }

  // ---------- Oppdatering ----------

  /**
   * Mute-toggle er identisk med tidligere DOM-handler: state speiles i
   * audioManager (som varsler voice-over) og ambient-loop slås av/på.
   * Også HiddenActions sender hit via samme metode.
   */
  toggleMute(): boolean {
    const erAvNa = audioManager.toggleMute();
    if (erAvNa) ambientAudio.slaaAv();
    else ambientAudio.slaaPaa();
    this.muteKnappTekst?.setText(erAvNa ? '🔇' : '🔊');
    return erAvNa;
  }

  private oppdaterSpeedSymbol(_nivaa: AutoSpeedNivaa): void {
    this.speedKnappTekst?.setText(autoSpeed.symbol());
  }

  private refresh(snap: GameStateData): void {
    this.kronerTekst?.setText(formaterKroner(snap.kroner));
    this.dagTekst?.setText(String(snap.dag));
    this.sesongTekst?.setText(sesongTekst(snap.dag));

    // Tier-chip-farge + tekst
    if (this.tierChipBg && this.tierChipTekst) {
      this.tierChipBg.setFillStyle(TIER_BG[snap.tier], 1);
      this.tierChipBg.setStrokeStyle(2, COLORS.white, 0.9);
      this.tierChipTekst.setText(`Tier ${snap.tier}`);
      this.tierChipTekst.setColor(TIER_TEKST_FARGE[snap.tier]);
    }

    // Omdømme-chip
    if (this.omdommeChipTekst) {
      const nivaa = omdommeNivaaTekst(snap.omdomme);
      this.omdommeChipTekst.setText(`Omdømme: ${nivaa} (${snap.omdomme})`);
    }

    // Speil til ARIA-live-region for skjermlesere.
    ariaMirror.varselHud({
      kroner: snap.kroner,
      dag: snap.dag,
      sesong: SESONG_NAVN[sesongForDag(snap.dag)],
      tier: snap.tier,
    });
  }
}

// Beholdt for å unngå å brekke andre import-er som kanskje refererer
// hit (nivå-tekst). Samme verdi som internt brukes i HUD-en.
export function omdommeNivaa(verdi: number): string {
  return omdommeNivaaTekst(verdi);
}

// Eksporter TIER_COLORS-ref for testbarhet (uendret signatur).
export const HUD_TIER_FARGER = TIER_COLORS;
